<template>
  <button
    v-if="!to"
    :class="buttonClass"
    :style="style"
    :disabled="disabled"
    :type="type"
    :title="label"
    :aria-disabled="disabled"
    @click="handleClick"
  >
    <slot name="icon" v-if="iconPosition === 'left'"></slot>
    <slot>
      <span :class="labelClass" class="button__label text-regular-semibold">
        {{ label }}
      </span>
    </slot>
    <slot name="icon" v-if="iconPosition === 'right'"></slot>
  </button>
  <NuxtLink
    v-else
    :to="to"
    :target="target"
    :class="buttonClass"
    :style="style"
    :disabled="disabled"
    :aria-disabled="disabled"
    :type="type"
    :title="t('menu.navigateTo', { label })"
    @click="handleClick"
  >
    <slot name="icon" v-if="iconPosition === 'left'"></slot>
    <slot>
      <span :class="labelClass" class="button__label text-regular-semibold">
        {{ label }}
      </span>
    </slot>
    <slot name="icon" v-if="iconPosition === 'right'"></slot>
  </NuxtLink>
</template>

<script lang="ts" setup>
  const { t } = useI18n();
  const emit = defineEmits<{
    (e: 'click'): void;
  }>();

  const props = withDefaults(
    defineProps<{
      label: string;
      variant?: 'primary' | 'secondary' | 'secondary_inverted' | 'orange' | 'accent';
      size?: 'small' | 'medium' | 'large';
      iconPosition?: 'left' | 'right';
      to?: string;
      target?: string;
      type?: 'button' | 'submit' | 'reset';
      disabled?: boolean;
      loading?: boolean;
      class?: string;
      labelClass?: string;
      style?: string;
    }>(),
    {
      variant: 'primary',
      size: 'medium',
      iconPosition: 'left',
      type: 'button',
      disabled: false,
      loading: false,
    }
  );

  const handleClick = () => {
    if (props.disabled) return;
    if (props.loading) return;
    emit('click');
  };

  const buttonClass = computed(() => {
    return `button button--${props.variant} button--${props.size} ${props.class ?? ''}`;
  });
</script>

<style lang="scss" scoped>
  .button {
    @apply flex items-center justify-center gap-2 px-6 py-3;
    @apply border border-primary rounded-sm;
    @apply transition-all duration-150;
    @apply cursor-pointer;
    text-decoration: none !important;

    &:hover {
      @apply bg-primary text-white;
    }

    &--primary {
      @apply bg-primary;
      .button__label {
        @apply text-white;
      }
      &:hover {
        @apply bg-blue-whale-darker;
      }

      &:disabled {
        @apply bg-neutral-lightest border-neutral-lightest;
        .button__label {
          @apply text-primary;
        }
      }
    }

    &--secondary {
      @apply bg-white;
      .button__label {
        @apply text-primary;
      }
      &:hover {
        @apply bg-blue-whale-lightest;
      }

      &:disabled {
        @apply bg-neutral-lightest border-neutral-lightest;
        .button__label {
          @apply text-primary;
        }
      }
    }

    &--orange {
      @apply border-christine-dark;
      @apply bg-christine;
      .button__label {
        @apply text-white;
      }
      &:hover {
        @apply bg-christine-dark;
      }

      &:disabled {
        @apply bg-neutral-lightest border-neutral-lightest;
        .button__label {
          @apply text-primary;
        }
      }
    }

    &--accent {
      @apply border-accent bg-accent;
      .button__label {
        @apply text-white;
      }
    }

    &--secondary_inverted {
      @apply border-white bg-transparent;
      .button__label {
        @apply text-white;
      }

      &:disabled {
        @apply bg-neutral-lightest border-neutral-lightest;
        .button__label {
          @apply text-white;
        }
      }
    }

    &--small {
      @apply px-5 py-2;
    }

    &--medium {
      @apply px-6 py-3;
    }

    &:disabled {
      @apply cursor-not-allowed;
    }

  }
</style>
